import React, { useState, useEffect, useCallback } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  CpuChipIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const Analytics = () => {
  const { tenantName } = useAuth();
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [bots, setBots] = useState([]);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(6);
  const [logFilters, setLogFilters] = useState({ status: '', direction: '' });
  const [exporting, setExporting] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const response = await api.get('/analytics/overview');
      setOverview(response.data);
    } catch (err) {
      console.error('Failed to fetch overview:', err);
    }
  }, []);

  const fetchTrend = useCallback(async () => {
    try {
      const response = await api.get(`/analytics/usage-trend?months=${months}`);
      setTrend(response.data.trend || []);
    } catch (err) {
      console.error('Failed to fetch trend:', err);
    }
  }, [months]);

  const fetchBotActivity = useCallback(async () => {
    try {
      const response = await api.get('/analytics/bot-activity');
      setBots(response.data.bots || []);
    } catch (err) {
      console.error('Failed to fetch bot activity:', err);
    }
  }, []);

  const fetchLogs = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (logFilters.status) params.append('status', logFilters.status);
      if (logFilters.direction) params.append('direction', logFilters.direction);

      const response = await api.get(`/analytics/execution-logs?${params}`);
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    }
  }, [logFilters]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchTrend(), fetchBotActivity(), fetchLogs()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchOverview, fetchTrend, fetchBotActivity, fetchLogs]);

  useEffect(() => {
    fetchTrend();
  }, [months, fetchTrend]);

  useEffect(() => {
    fetchLogs(1);
  }, [logFilters, fetchLogs]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get(`/analytics/export?months=${months}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `chatza-analytics.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to download report');
    } finally {
      setExporting(false);
    }
  };

  const calcChange = (current, previous) => {
    if (previous === 0) return current > 0 ? '+100%' : '0%';
    const change = ((current - previous) / previous * 100).toFixed(0);
    return change >= 0 ? `+${change}%` : `${change}%`;
  };

  const isPositiveChange = (current, previous) => current >= previous;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color = 'primary', change, positive }) => (
    <div className="stat-card group">
      <div className="flex items-start justify-between">
        <div className={`stat-icon bg-${color}-50 text-${color}-500`}>
          <Icon className="w-6 h-6" />
        </div>
        {change && (
          <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
            positive ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'
          }`}>
            {positive ? <ArrowTrendingUpIcon className="w-3 h-3" /> : <ArrowTrendingDownIcon className="w-3 h-3" />}
            {change}
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="stat-value">{value}</p>
        <p className="stat-label">{label}</p>
      </div>
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-neutral-800 mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
            {entry.name}: <span className="font-semibold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="page-header">
          <h1 className="page-title">Analytics & Reports</h1>
          <p className="page-subtitle">
            {tenantName ? `${tenantName} — ` : ''}Monitor your chatbot performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value))}
            className="input-field text-sm py-2 px-3 w-auto"
          >
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn-outline btn-sm inline-flex items-center gap-2"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Download CSV'}
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
        <StatCard
          icon={ChatBubbleLeftRightIcon}
          label="Conversations This Month"
          value={overview?.currentMonth?.conversations || 0}
          color="primary"
          change={overview ? calcChange(overview.currentMonth.conversations, overview.previousMonth.conversations) : null}
          positive={overview ? isPositiveChange(overview.currentMonth.conversations, overview.previousMonth.conversations) : true}
        />
        <StatCard
          icon={EnvelopeIcon}
          label="Messages This Month"
          value={overview?.currentMonth?.messages || 0}
          color="blue"
          change={overview ? calcChange(overview.currentMonth.messages, overview.previousMonth.messages) : null}
          positive={overview ? isPositiveChange(overview.currentMonth.messages, overview.previousMonth.messages) : true}
        />
        <StatCard
          icon={CheckCircleIcon}
          label="Success Rate"
          value={`${overview?.currentMonth?.successRate || 100}%`}
          color={overview?.currentMonth?.successRate >= 90 ? 'green' : overview?.currentMonth?.successRate >= 70 ? 'yellow' : 'red'}
        />
        <StatCard
          icon={CpuChipIcon}
          label="Active Bots"
          value={overview?.currentMonth?.activeBots || 0}
          color="purple"
        />
      </div>

      {/* Plan usage indicator */}
      {overview?.plan?.messageLimit && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-neutral-700">
              Monthly Usage — {overview.plan.name} Plan
            </span>
            <span className="text-sm text-neutral-500">
              {overview.currentMonth.conversations} / {overview.plan.messageLimit} conversations
            </span>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${
                (overview.currentMonth.conversations / overview.plan.messageLimit) > 0.9
                  ? 'bg-red-500' : (overview.currentMonth.conversations / overview.plan.messageLimit) > 0.7
                  ? 'bg-yellow-500' : 'bg-primary-500'
              }`}
              style={{ width: `${Math.min((overview.currentMonth.conversations / overview.plan.messageLimit) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Usage Trend Chart */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-800">Usage Trend</h3>
            <p className="text-sm text-neutral-500 mt-1">Messages and conversations over time</p>
          </div>
        </div>
        {trend.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={trend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorConversations" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '13px', paddingTop: '8px' }}
              />
              <Area
                type="monotone"
                dataKey="messages"
                name="Messages"
                stroke="#f97316"
                strokeWidth={2}
                fill="url(#colorMessages)"
                dot={{ r: 4, fill: '#f97316' }}
                activeDot={{ r: 6 }}
              />
              <Area
                type="monotone"
                dataKey="conversations"
                name="Conversations"
                stroke="#22c55e"
                strokeWidth={2}
                fill="url(#colorConversations)"
                dot={{ r: 4, fill: '#22c55e' }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-48 text-neutral-400">
            <p>No usage data available yet</p>
          </div>
        )}
      </div>

      {/* Bot Activity Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-800">Bot Activity</h3>
            <p className="text-sm text-neutral-500 mt-1">Per-bot performance this month</p>
          </div>
        </div>
        {bots.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className="text-left py-3 px-4 font-medium text-neutral-500">Bot Name</th>
                  <th className="text-right py-3 px-4 font-medium text-neutral-500">Executions</th>
                  <th className="text-right py-3 px-4 font-medium text-neutral-500">Sessions</th>
                  <th className="text-right py-3 px-4 font-medium text-neutral-500">Success Rate</th>
                  <th className="text-right py-3 px-4 font-medium text-neutral-500">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {bots.map((bot) => (
                  <tr key={bot.typebotId} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                    <td className="py-3 px-4 font-medium text-neutral-800">{bot.name}</td>
                    <td className="py-3 px-4 text-right text-neutral-600">{bot.totalExecutions}</td>
                    <td className="py-3 px-4 text-right text-neutral-600">{bot.uniqueSessions}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`badge ${
                        parseFloat(bot.successRate) >= 90 ? 'badge-green' :
                        parseFloat(bot.successRate) >= 70 ? 'badge-yellow' : 'badge-red'
                      }`}>
                        {bot.successRate}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-neutral-500 text-xs">
                      {formatDate(bot.lastActivity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
            <CpuChipIcon className="w-12 h-12 mb-3 text-neutral-300" />
            <p className="font-medium text-neutral-500">No bot activity this month</p>
            <p className="text-sm mt-1">Bot stats will appear here once conversations start</p>
          </div>
        )}
      </div>

      {/* Execution Logs */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-800">Execution Logs</h3>
            <p className="text-sm text-neutral-500 mt-1">Detailed activity log</p>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-neutral-400" />
            <select
              value={logFilters.status}
              onChange={(e) => setLogFilters(prev => ({ ...prev, status: e.target.value }))}
              className="input-field text-sm py-1.5 px-2 w-auto"
            >
              <option value="">All statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={logFilters.direction}
              onChange={(e) => setLogFilters(prev => ({ ...prev, direction: e.target.value }))}
              className="input-field text-sm py-1.5 px-2 w-auto"
            >
              <option value="">All directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>
        </div>

        {logs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-3 px-4 font-medium text-neutral-500">Time</th>
                    <th className="text-left py-3 px-4 font-medium text-neutral-500">Direction</th>
                    <th className="text-left py-3 px-4 font-medium text-neutral-500">Preview</th>
                    <th className="text-left py-3 px-4 font-medium text-neutral-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                      <td className="py-3 px-4 text-neutral-500 text-xs whitespace-nowrap">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`badge text-xs ${
                          log.direction === 'inbound' ? 'badge-blue' : 'badge-purple'
                        }`}>
                          {log.direction}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-neutral-600 max-w-xs truncate">
                        {log.messagePreview || '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`badge text-xs ${
                          log.status === 'success' ? 'badge-green' : 'badge-red'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-neutral-200 mt-4">
                <p className="text-sm text-neutral-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchLogs(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="btn-outline btn-sm p-1.5 disabled:opacity-40"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-medium text-neutral-700">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => fetchLogs(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn-outline btn-sm p-1.5 disabled:opacity-40"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
            <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 text-neutral-300" />
            <p className="font-medium text-neutral-500">No execution logs found</p>
            <p className="text-sm mt-1">Logs will appear here once bot conversations start</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
